import React, { useState } from 'react';
import { RefreshCw, Zap, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ScrapeButtonProps {
  onScrapeComplete: () => void;
}

export function ScrapeButton({ onScrapeComplete }: ScrapeButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleScrape = async () => {
    setIsLoading(true);
    setStatus('idle');
    setMessage('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scrape-orchestrator`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sources: ['github', 'huggingface'],
            forceRefresh: true
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Scraping failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        const totalAgents = data.results.reduce((sum: number, result: any) => 
          sum + (result.agents_found || 0), 0
        );
        
        setStatus('success');
        setMessage(`Successfully scraped ${totalAgents} agents from ${data.results.length} sources`);
        onScrapeComplete();
      } else {
        throw new Error(data.error || 'Scraping failed');
      }
    } catch (error) {
      console.error('Scrape error:', error);
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Scraping failed');
    } finally {
      setIsLoading(false);
      
      // Clear status after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    }
  };

  return (
    <div className="flex items-center gap-2 md:gap-4">
      <button
        onClick={handleScrape}
        disabled={isLoading}
        className="flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-xl hover:from-primary-700 hover:to-accent-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-medium hover:shadow-strong hover:scale-105 font-medium text-sm md:text-base"
      >
        {isLoading ? (
          <>
            <RefreshCw className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
            <span className="hidden xs:inline">Scraping...</span>
          </>
        ) : (
          <>
            <Zap className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden xs:inline">Discover</span> Agents
          </>
        )}
      </button>

      {/* Status Message */}
      {message && (
        <div className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border-2 ${
          status === 'success' 
            ? 'bg-green-50 text-green-700 border-green-200' 
            : 'bg-red-50 text-red-700 border-red-200'
        }`}>
          {status === 'success' ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          {message}
        </div>
      )}
    </div>
  );
}